// Team Management Page - Admin only - View and manage operators
import React, { useState, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import AdminHeader from '../../../shared/components/AdminHeader';
import AdminDataTable from '../../../shared/components/AdminDataTable';
import TabBar from '../../../shared/components/TabBar';
import ReassignModal from '../../work/components/ReassignModal';

interface TeamManagementProps {
  brand: BrandConfig;
  Icons: IconsType;
  onNavigate: (module: string, subSection?: string) => void;
}

type OperatorStatus = 'available' | 'busy' | 'away' | 'offline';

interface Operator {
  id: string;
  displayName: string;
  email: string;
  platformRole: string;
  status: OperatorStatus;
  maxWorkload: number;
  currentWorkload: number;
  activeItems: number;
  completedToday: number;
  avgResolutionTime: string;
  complianceRate: number;
  [key: string]: unknown;
}

// Mock operators data
const mockOperators: Operator[] = [
  {
    id: '660e8400-e29b-41d4-a716-446655440001',
    displayName: 'Operator One',
    email: 'operator1@test.com',
    platformRole: 'operator',
    status: 'available',
    maxWorkload: 10,
    currentWorkload: 3,
    activeItems: 3,
    completedToday: 5,
    avgResolutionTime: '2.5h',
    complianceRate: 94,
  },
  {
    id: '660e8400-e29b-41d4-a716-446655440002',
    displayName: 'Operator Two',
    email: 'operator2@test.com',
    platformRole: 'operator',
    status: 'busy',
    maxWorkload: 8,
    currentWorkload: 6,
    activeItems: 6,
    completedToday: 3,
    avgResolutionTime: '3.2h',
    complianceRate: 82,
  },
  {
    id: '660e8400-e29b-41d4-a716-446655440003',
    displayName: 'Admin User',
    email: 'admin@test.com',
    platformRole: 'admin',
    status: 'available',
    maxWorkload: 15,
    currentWorkload: 2,
    activeItems: 2,
    completedToday: 8,
    avgResolutionTime: '1.8h',
    complianceRate: 98,
  },
  {
    id: '660e8400-e29b-41d4-a716-446655440004',
    displayName: 'Operator Three',
    email: 'operator3@test.com',
    platformRole: 'operator',
    status: 'away',
    maxWorkload: 10,
    currentWorkload: 0,
    activeItems: 0,
    completedToday: 2,
    avgResolutionTime: '2.8h',
    complianceRate: 89,
  },
  {
    id: '660e8400-e29b-41d4-a716-446655440005',
    displayName: 'Operator Four',
    email: 'operator4@test.com',
    platformRole: 'operator',
    status: 'offline',
    maxWorkload: 12,
    currentWorkload: 0,
    activeItems: 0,
    completedToday: 0,
    avgResolutionTime: '-',
    complianceRate: 91,
  },
];

// Mock assignments for reassignment (will be used when reassign feature is complete)
const _mockAssignments = [
  { id: 'a1', entityType: 'order', entity: { title: 'Brown v. Taylor' }, assigneeId: '660e8400-e29b-41d4-a716-446655440001' },
  { id: 'a2', entityType: 'case', entity: { title: 'Garcia v. Martinez' }, assigneeId: '660e8400-e29b-41d4-a716-446655440001' },
  { id: 'a3', entityType: 'filing', entity: { title: 'Motion Response' }, assigneeId: '660e8400-e29b-41d4-a716-446655440002' },
];

const statusColors = {
  available: '#10B981',
  busy: '#F59E0B',
  away: '#6B7280',
  offline: '#EF4444',
};

const statusLabels = {
  available: 'Available',
  busy: 'Busy',
  away: 'Away',
  offline: 'Offline',
};

const TeamManagement: React.FC<TeamManagementProps> = ({ brand, Icons, onNavigate: _onNavigate }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [operators, setOperators] = useState(mockOperators);
  const [_selectedOperator, setSelectedOperator] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  void _onNavigate; // Will be used for navigation
  void setOperators; // Will be used for API updates
  void _selectedOperator; // Will be used for detail view

  // Filter tabs
  const filterTabs = [
    { id: 'all', label: 'All Operators', count: operators.length },
    { id: 'online', label: 'Online', count: operators.filter(o => o.status !== 'offline').length },
    { id: 'available', label: 'Available', count: operators.filter(o => o.status === 'available').length },
    { id: 'operators', label: 'Operators', count: operators.filter(o => o.platformRole === 'operator').length },
    { id: 'admins', label: 'Admins', count: operators.filter(o => o.platformRole === 'admin').length },
  ];

  // Apply filters
  const filteredOperators = useMemo(() => {
    let items = [...operators];

    switch (activeFilter) {
      case 'online':
        items = items.filter(o => o.status !== 'offline');
        break;
      case 'available':
        items = items.filter(o => o.status === 'available');
        break;
      case 'operators':
        items = items.filter(o => o.platformRole === 'operator');
        break;
      case 'admins':
        items = items.filter(o => o.platformRole === 'admin');
        break;
      default:
        break;
    }

    return items;
  }, [operators, activeFilter]);

  const getWorkloadColor = (current, max) => {
    const ratio = current / max;
    if (ratio >= 1) return brand?.colors?.error || '#EF4444';
    if (ratio >= 0.8) return brand?.colors?.warning || '#F59E0B';
    return brand?.colors?.success || '#10B981';
  };

  const getComplianceColor = (rate) => {
    if (rate >= 95) return '#10B981';
    if (rate >= 85) return '#F59E0B';
    return '#EF4444';
  };

  const handleReassign = async ({ assignmentId: _assignmentId, newAssigneeId: _newAssigneeId, notes: _notes }) => {
    // Simulate API call - params will be used for actual API call
    void _assignmentId;
    void _newAssigneeId;
    void _notes;
    await new Promise(resolve => setTimeout(resolve, 1000));
    setShowReassignModal(false);
    setSelectedAssignment(null);
    // In production, update the assignments list
  };

  // Table columns
  const columns = [
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <div className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: statusColors[value] }}
          />
          <span className="text-sm" style={{ color: brand.colors.text }}>
            {statusLabels[value]}
          </span>
        </div>
      ),
    },
    {
      key: 'displayName',
      label: 'Operator',
      render: (value, row) => (
        <div>
          <div className="font-medium" style={{ color: brand.colors.text }}>{value}</div>
          <div className="text-xs" style={{ color: brand.colors.textLight }}>{row.email}</div>
        </div>
      ),
    },
    {
      key: 'platformRole',
      label: 'Role',
      render: (value) => (
        <span
          className="px-2 py-0.5 rounded-sm text-xs font-medium capitalize"
          style={{
            backgroundColor: value === 'admin' ? '#8B5CF620' : '#3B82F620',
            color: value === 'admin' ? '#7C3AED' : '#2563EB',
          }}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'currentWorkload',
      label: 'Workload',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min((value / row.maxWorkload) * 100, 100)}%`,
                backgroundColor: getWorkloadColor(value, row.maxWorkload),
              }}
            />
          </div>
          <span
            className="text-sm font-medium"
            style={{ color: getWorkloadColor(value, row.maxWorkload) }}
          >
            {value}/{row.maxWorkload}
          </span>
        </div>
      ),
    },
    {
      key: 'completedToday',
      label: 'Completed Today',
      render: (value) => (
        <span className="text-sm font-medium" style={{ color: brand.colors.text }}>
          {value}
        </span>
      ),
    },
    {
      key: 'avgResolutionTime',
      label: 'Avg Resolution',
      render: (value) => (
        <span className="text-sm" style={{ color: brand.colors.textLight }}>
          {value}
        </span>
      ),
    },
    {
      key: 'complianceRate',
      label: 'SLA Compliance',
      render: (value) => (
        <span
          className="text-sm font-medium"
          style={{ color: getComplianceColor(value) }}
        >
          {value}%
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <button
          onClick={() => setSelectedOperator(row)}
          className="px-2 py-1 text-xs rounded-sm border transition-colors hover:bg-gray-50"
          style={{ borderColor: brand.colors.border, color: brand.colors.link }}
        >
          View Details
        </button>
      ),
    },
  ];

  // Calculate team stats
  const teamStats = useMemo(() => {
    const online = operators.filter(o => o.status !== 'offline');
    const totalWorkload = online.reduce((sum, o) => sum + o.currentWorkload, 0);
    const totalCapacity = online.reduce((sum, o) => sum + o.maxWorkload, 0);
    const totalCompleted = operators.reduce((sum, o) => sum + o.completedToday, 0);
    const avgCompliance = Math.round(operators.reduce((sum, o) => sum + o.complianceRate, 0) / operators.length);

    return {
      online: online.length,
      available: operators.filter(o => o.status === 'available').length,
      totalWorkload,
      totalCapacity,
      utilizationRate: totalCapacity > 0 ? Math.round((totalWorkload / totalCapacity) * 100) : 0,
      totalCompleted,
      avgCompliance,
    };
  }, [operators]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: brand.colors.bgLight }}>
      <AdminHeader
        title="Team Management"
        subtitle="Manage operators and workload distribution"
        brand={brand}
        Icons={Icons}
      />

      {/* Summary Cards */}
      <div className="px-6 py-4 border-b" style={{ backgroundColor: 'white', borderColor: brand.colors.border }}>
        <div className="flex gap-4 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#D1FAE5' }}>
            <span className="text-2xl font-bold" style={{ color: '#059669' }}>{teamStats.online}</span>
            <span className="text-sm" style={{ color: '#065F46' }}>Online</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#DBEAFE' }}>
            <span className="text-2xl font-bold" style={{ color: '#2563EB' }}>{teamStats.available}</span>
            <span className="text-sm" style={{ color: '#1E40AF' }}>Available</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: brand.colors.bgLight }}>
            <span className="text-2xl font-bold" style={{ color: brand.colors.text }}>
              {teamStats.totalWorkload}/{teamStats.totalCapacity}
            </span>
            <span className="text-sm" style={{ color: brand.colors.textLight }}>
              Total Workload ({teamStats.utilizationRate}%)
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#F3E8FF' }}>
            <span className="text-2xl font-bold" style={{ color: '#7C3AED' }}>{teamStats.totalCompleted}</span>
            <span className="text-sm" style={{ color: '#6D28D9' }}>Completed Today</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: getComplianceColor(teamStats.avgCompliance) + '20' }}>
            <span className="text-2xl font-bold" style={{ color: getComplianceColor(teamStats.avgCompliance) }}>
              {teamStats.avgCompliance}%
            </span>
            <span className="text-sm" style={{ color: brand.colors.textLight }}>Avg Compliance</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <TabBar
        tabs={filterTabs}
        activeTab={activeFilter}
        onTabChange={setActiveFilter}
        brand={brand}
      />

      {/* Operators Table */}
      <div className="flex-1 overflow-auto p-6">
        <div
          className="rounded-xl overflow-hidden"
          style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
        >
          <AdminDataTable
            columns={columns}
            data={filteredOperators}
            brand={brand}
            Icons={Icons}
          />
        </div>
      </div>

      {/* Reassign Modal */}
      <ReassignModal
        isOpen={showReassignModal}
        onClose={() => {
          setShowReassignModal(false);
          setSelectedAssignment(null);
        }}
        onReassign={handleReassign}
        assignment={selectedAssignment}
        operators={operators.filter(o => o.status !== 'offline')}
        brand={brand}
      />
    </div>
  );
};

export default TeamManagement;
